import { create } from 'zustand'

// ─────────────────────────────────────────────────────────────────────────────
// Theme = palette × mode (DECISIONS §26).
//
//   palette  neon (iOS hues fully saturated and glowing: the brand, default)
//            | aurora (the original neon look) | ios (Apple system colours)
//   mode     auto (follows the device, default) | light | dark
//
// The CSS lives in index.css (`:root[data-theme][data-mode]`). The inline
// script in index.html applies the saved choice before first paint so there
// is no flash; this module keeps it in sync after that. Saved per device in
// localStorage under one key.
// ─────────────────────────────────────────────────────────────────────────────

export type ThemePalette = 'aurora' | 'ios' | 'neon'
export type ThemeMode = 'auto' | 'light' | 'dark'

export const THEME_STORAGE_KEY = 'cb-theme'

export const PALETTES: { id: ThemePalette; label: string; hint: string }[] = [
  { id: 'neon', label: 'iOS Neon', hint: 'iOS colours turned all the way up, with glow' },
  { id: 'aurora', label: 'Aurora', hint: 'Neon colour with a soft glow' },
  { id: 'ios', label: 'iOS', hint: "Apple's system colours, flat and clean" },
]

export const MODES: { id: ThemeMode; label: string }[] = [
  { id: 'auto', label: 'Auto' },
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
]

interface Saved { palette: ThemePalette; mode: ThemeMode }

function readSaved(): Saved {
  try {
    const raw = JSON.parse(localStorage.getItem(THEME_STORAGE_KEY) ?? 'null') as Partial<Saved> | null
    return {
      palette: raw?.palette === 'ios' || raw?.palette === 'aurora' ? raw.palette : 'neon',
      mode: raw?.mode === 'light' || raw?.mode === 'dark' ? raw.mode : 'auto',
    }
  } catch {
    return { palette: 'neon', mode: 'auto' }
  }
}

const darkQuery = (): MediaQueryList | null =>
  typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null

export function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'auto') return mode
  return darkQuery()?.matches === false ? 'light' : 'dark'
}

/** Writes the theme onto <html> plus the browser chrome that can't read CSS
 *  variables (theme-color, the iOS status bar). Mirrors the index.html script. */
export function applyTheme(palette: ThemePalette, mode: ThemeMode): void {
  const resolved = resolveMode(mode)
  const root = document.documentElement
  root.dataset.theme = palette
  root.dataset.mode = resolved
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
  const canvas = resolved === 'dark' ? '#000000' : palette === 'ios' ? '#F2F2F7' : '#FFFFFF'
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', canvas)
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', resolved)
  document
    .querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
    ?.setAttribute('content', resolved === 'dark' ? 'black' : 'default')
}

interface ThemeState extends Saved {
  /** What is on screen now (auto resolved). */
  resolved: 'light' | 'dark'
  setPalette: (palette: ThemePalette) => void
  setMode: (mode: ThemeMode) => void
}

function save(s: Saved) {
  try { localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(s)) } catch { /* private mode: still applies */ }
}

const initial = readSaved()

export const useThemeStore = create<ThemeState>((set, get) => ({
  ...initial,
  resolved: resolveMode(initial.mode),
  setPalette: (palette) => {
    const { mode } = get()
    save({ palette, mode })
    applyTheme(palette, mode)
    set({ palette })
  },
  setMode: (mode) => {
    const { palette } = get()
    save({ palette, mode })
    applyTheme(palette, mode)
    set({ mode, resolved: resolveMode(mode) })
  },
}))

/** Call once at startup: applies the saved theme and follows the device when on Auto. */
export function initTheme(): void {
  const { palette, mode } = useThemeStore.getState()
  applyTheme(palette, mode)
  const q = darkQuery()
  q?.addEventListener?.('change', () => {
    const s = useThemeStore.getState()
    if (s.mode !== 'auto') return
    applyTheme(s.palette, 'auto')
    useThemeStore.setState({ resolved: resolveMode('auto') })
  })
}
